import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { discoveryAPI } from '@/lib/api';
import { useAuth } from '@/stores/auth-store';
import { useTeams } from '@/hooks/use-teams';
import { 
  Search, 
  Brain, 
  Users, 
  TrendingUp, 
  MapPin, 
  Building2, 
  Mail,
  Phone,
  Globe,
  Star,
  Clock,
  CheckCircle,
  AlertCircle,
  Play,
  Pause,
  Download,
  Filter,
  Eye,
  History,
  FileText,
  ExternalLink,
  Linkedin,
  Calendar,
  Target,
  TrendingDown,
  RefreshCw,
  Database,
  Import
} from 'lucide-react';
import { 
  DiscoverySession,
  DiscoverySessionWithDetails,
  DiscoverySessionLead,
  CreateSessionRequest,
  SessionStats
} from '@/types/discovery';

export function DiscoveryPage() {
  const { user } = useAuth();
  const { currentTeam, getCurrentTeamId } = useTeams();
  const [activeTab, setActiveTab] = useState('sessions');
  const [currentSession, setCurrentSession] = useState<DiscoverySessionWithDetails | null>(null);
  const [sessions, setSessions] = useState<DiscoverySessionWithDetails[]>([]);
  const [sessionLeads, setSessionLeads] = useState<DiscoverySessionLead[]>([]);
  const [selectedLeads, setSelectedLeads] = useState<Set<number>>(new Set());
  const [sessionStats, setSessionStats] = useState<SessionStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [sessionPolling, setSessionPolling] = useState<NodeJS.Timeout | null>(null);
  const [realtimeProgress, setRealtimeProgress] = useState<any>(null);
  const [progressPolling, setProgressPolling] = useState<NodeJS.Timeout | null>(null);

  // Session creation form state
  const [sessionForm, setSessionForm] = useState<CreateSessionRequest>({
    name: '',
    description: '',
    discoveryConfig: {
      targetCount: 100,
      industries: [],
      companySize: [],
      services: [],
      geography: [],
      customCriteria: ''
    }
  });

  // Filter state for session leads
  const [leadFilters, setLeadFilters] = useState({
    minScore: 0, // Show all leads by default, including low-quality ones
    maxScore: 100,
    limit: 100,
    offset: 0,
    isSelected: undefined as boolean | undefined,
    isImported: undefined as boolean | undefined
  });

  useEffect(() => {
    loadSessions();
    loadActiveSession();
    return () => {
      if (sessionPolling) {
        clearInterval(sessionPolling);
      }
      if (progressPolling) {
        clearInterval(progressPolling);
      }
    };
  }, []);

  const loadSessions = async () => {
    const teamId = getCurrentTeamId();
    if (!teamId) return;
    
    try {
      const response = await discoveryAPI.getSessions(teamId, { limit: 20 });
      if (response.success) {
        setSessions(response.data as DiscoverySessionWithDetails[]);
      }
    } catch (error) {
      console.error('Failed to load discovery sessions:', error);
    }
  };

  const loadActiveSession = async () => {
    const teamId = getCurrentTeamId();
    if (!teamId) return;
    
    try {
      const response = await discoveryAPI.getActiveSession(teamId);
      if (response.success && response.data) {
        const sessionData = response.data as DiscoverySessionWithDetails;
        setCurrentSession(sessionData);
        if (sessionData.status === 'processing') {
          startSessionPolling(sessionData.id);
        }
        // Auto-load leads if session has results
        if (sessionData.totalLeadsFound > 0) {
          loadSessionLeads(sessionData.id);
        }
      }
    } catch (error) {
      console.error('Failed to load active session:', error);
    }
  };

  const createSession = async () => {
    const teamId = getCurrentTeamId();
    if (!teamId) {
      toast({ title: "Error", description: "Please select a team first", variant: "destructive" });
      return;
    }

    if (!sessionForm.name.trim()) {
      toast({ title: "Error", description: "Please enter a session name", variant: "destructive" });
      return;
    }

    setIsCreating(true);
    try {
      const response = await discoveryAPI.createSession(teamId, sessionForm);
      if (response.success) {
        const sessionData = response.data as DiscoverySession;
        toast({ 
          title: "Session Created", 
          description: `Discovery session "${sessionData.name}" has been created.` 
        });
        
        // Reset form
        setSessionForm({
          name: '',
          description: '',
          discoveryConfig: {
            targetCount: 100,
            industries: [],
            companySize: [],
            services: [],
            geography: [],
            customCriteria: ''
          }
        });
        
        // Reload sessions and set as current
        await loadSessions();
        setCurrentSession(sessionData as DiscoverySessionWithDetails);
        setActiveTab('current');
      }
    } catch (error: any) {
      toast({ 
        title: "Failed to Create Session", 
        description: error.response?.data?.message || "An error occurred",
        variant: "destructive" 
      });
    } finally {
      setIsCreating(false);
    }
  };

  const startSessionPolling = (sessionId: number) => {
    if (sessionPolling) {
      clearInterval(sessionPolling);
    }
    
    const polling = setInterval(async () => {
      await checkSessionStatus(sessionId);
    }, 5000);
    setSessionPolling(polling);
    
    // Also start progress polling for real-time updates
    startProgressPolling(sessionId);
  };

  const startProgressPolling = (sessionId: number) => {
    if (progressPolling) {
      clearInterval(progressPolling);
    }
    
    // Poll progress every 3 seconds for more responsive updates
    const interval = setInterval(() => {
      fetchRealtimeProgress(sessionId);
    }, 3000);
    
    setProgressPolling(interval);
  };

  const stopProgressPolling = () => {
    if (progressPolling) {
      clearInterval(progressPolling);
      setProgressPolling(null);
    }
    setRealtimeProgress(null);
  };

  const fetchRealtimeProgress = async (sessionId: number) => {
    const teamId = getCurrentTeamId();
    if (!teamId) return;

    try {
      const response = await discoveryAPI.getSessionProgress(teamId, sessionId);
      if (response.success) {
        setRealtimeProgress(response.data);
        
        // Update session data if available
        if (response.data.session) {
          setCurrentSession(prev => prev ? { ...prev, ...response.data.session } : null);
        }
        
        // Stop polling if session is no longer active
        if (response.data.session?.status !== 'processing') {
          stopProgressPolling();
        }
      }
    } catch (error) {
      console.error('Failed to fetch realtime progress:', error);
    }
  };

  const checkSessionStatus = async (sessionId: number) => {
    const teamId = getCurrentTeamId();
    if (!teamId) return;

    try {
      const response = await discoveryAPI.getSession(teamId, sessionId);
      if (response.success) {
        const sessionData = response.data as DiscoverySessionWithDetails;
        setCurrentSession(sessionData);
        
        // Stop polling if session is complete, failed, or paused
        if (sessionData.status === 'completed' || 
            sessionData.status === 'failed' ||
            sessionData.status === 'paused') {
          if (sessionPolling) {
            clearInterval(sessionPolling);
            setSessionPolling(null);
          }
          stopProgressPolling();
          
          if (sessionData.status === 'completed') {
            await loadSessionLeads(sessionId);
            toast({ 
              title: "Discovery Complete", 
              description: `Found ${sessionData.totalLeadsFound} leads in session "${sessionData.name}".` 
            });
          }
        }
        
        // Update sessions list
        setSessions(prev => prev.map(s => s.id === sessionId ? sessionData : s));
      }
    } catch (error) {
      console.error('Failed to check session status:', error);
    }
  };

  const loadSessionLeads = async (sessionId: number) => {
    const teamId = getCurrentTeamId();
    if (!teamId) return;

    setIsLoading(true);
    try {
      const response = await discoveryAPI.getSessionLeads(teamId, sessionId, leadFilters);
      if (response.success) {
        setSessionLeads(response.data as DiscoverySessionLead[]);
        
        // Load session stats
        const statsResponse = await discoveryAPI.getSessionStats(teamId, sessionId);
        if (statsResponse.success) {
          setSessionStats(statsResponse.data as SessionStats);
        }
      }
    } catch (error: any) {
      toast({ 
        title: "Failed to Load Session Leads", 
        description: error.response?.data?.message || "An error occurred",
        variant: "destructive" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resumeSession = async (sessionId: number) => {
    const teamId = getCurrentTeamId();
    if (!teamId) return;

    setIsLoading(true);
    try {
      const response = await discoveryAPI.resumeSession(teamId, sessionId);
      if (response.success) {
        const sessionData = response.data as DiscoverySession;
        setCurrentSession(sessionData as DiscoverySessionWithDetails);
        startSessionPolling(sessionId);
        toast({ 
          title: "Session Resumed", 
          description: `Discovery session "${sessionData.name}" has been resumed.` 
        });
      }
    } catch (error: any) {
      toast({ 
        title: "Failed to Resume Session", 
        description: error.response?.data?.message || "An error occurred",
        variant: "destructive" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const pauseSession = async (sessionId: number) => {
    const teamId = getCurrentTeamId();
    if (!teamId) return;

    setIsLoading(true);
    try {
      const response = await discoveryAPI.pauseSession(teamId, sessionId);
      if (response.success) {
        const sessionData = response.data as DiscoverySession;
        setCurrentSession(sessionData as DiscoverySessionWithDetails);
        if (sessionPolling) {
          clearInterval(sessionPolling);
          setSessionPolling(null);
        }
        toast({ 
          title: "Session Paused", 
          description: `Discovery session "${sessionData.name}" has been paused.` 
        });
      }
    } catch (error: any) {
      toast({ 
        title: "Failed to Pause Session", 
        description: error.response?.data?.message || "An error occurred",
        variant: "destructive" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleLeadSelection = async (leadId: number) => {
    const newSelection = new Set(selectedLeads);
    const isSelected = !newSelection.has(leadId);
    
    if (isSelected) {
      newSelection.add(leadId);
    } else {
      newSelection.delete(leadId);
    }
    setSelectedLeads(newSelection);
    
    // Update selection in database
    if (currentSession) {
      const teamId = getCurrentTeamId();
      if (teamId) {
        try {
          await discoveryAPI.updateLeadSelection(teamId, currentSession.id, {
            leadIds: [leadId],
            isSelected
          });
        } catch (error) {
          console.error('Failed to update lead selection:', error);
        }
      }
    }
  };

  const selectAllLeads = async () => {
    if (!sessionLeads.length || !currentSession) return;
    const teamId = getCurrentTeamId();
    if (!teamId) return;

    const allLeadIds = new Set(sessionLeads.map(lead => lead.id));
    setSelectedLeads(allLeadIds);
    
    try {
      await discoveryAPI.updateLeadSelection(teamId, currentSession.id, {
        leadIds: Array.from(allLeadIds),
        isSelected: true
      });
    } catch (error) {
      console.error('Failed to select all leads:', error);
    }
  };

  const clearSelection = async () => {
    if (!currentSession || selectedLeads.size === 0) {
      setSelectedLeads(new Set());
      return;
    }
    
    const teamId = getCurrentTeamId();
    if (!teamId) return;

    try {
      await discoveryAPI.updateLeadSelection(teamId, currentSession.id, {
        leadIds: Array.from(selectedLeads),
        isSelected: false
      });
      setSelectedLeads(new Set());
    } catch (error) {
      console.error('Failed to clear selection:', error);
    }
  };

  const importSelectedLeads = async () => {
    if (!currentSession || selectedLeads.size === 0) return;
    const teamId = getCurrentTeamId();
    if (!teamId) return;

    setIsLoading(true);
    try {
      const response = await discoveryAPI.importSelectedLeads(teamId, currentSession.id);
      if (response.success) {
        const importedCount = response.data as number;
        toast({ 
          title: "Leads Imported", 
          description: `Successfully imported ${importedCount} leads to your main leads database.` 
        });
        
        // Reload session leads to update imported status
        await loadSessionLeads(currentSession.id);
        setSelectedLeads(new Set());
      }
    } catch (error: any) {
      toast({ 
        title: "Failed to Import Leads", 
        description: error.response?.data?.message || "An error occurred",
        variant: "destructive" 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'processing':
        return <Play className="h-4 w-4 text-blue-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'paused':
        return <Pause className="h-4 w-4 text-orange-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'yellow';
      case 'processing':
        return 'blue';
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      case 'paused':
        return 'orange';
      default:
        return 'gray';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 60) return 'text-blue-600 bg-blue-50 border-blue-200';
    if (score >= 40) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Brain className="h-8 w-8 text-blue-600" />
            Intelligent Discovery
          </h1>
          <p className="text-muted-foreground">
            AI-powered customer discovery and lead generation with persistent sessions
            {currentTeam && <span className="ml-2 text-blue-600">• Team: {currentTeam.name}</span>}
          </p>
        </div>
        
        {currentSession && (
          <Card className="w-64">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Current Session</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <div className="flex justify-between text-sm">
                <span>Status:</span>
                <Badge variant={currentSession.status === 'completed' ? 'default' : 'secondary'}>
                  {currentSession.status}
                </Badge>
              </div>
              <div className="flex justify-between text-sm">
                <span>Leads Found:</span>
                <span className="font-medium">{currentSession.totalLeadsFound}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Progress:</span>
                <span className="font-medium">{currentSession.progress}%</span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="sessions">Sessions</TabsTrigger>
          <TabsTrigger value="create">Create New</TabsTrigger>
          <TabsTrigger value="current">Current Session</TabsTrigger>
          <TabsTrigger value="leads">Discovered Leads</TabsTrigger>
        </TabsList>

        {/* Sessions Tab */}
        <TabsContent value="sessions" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Discovery Sessions
              </CardTitle>
              <CardDescription>
                View and manage your discovery sessions. Sessions persist across page reloads.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sessions.length > 0 ? (
                <div className="space-y-4">
                  {sessions.map((session) => (
                    <div key={session.id} className="p-4 border rounded-lg space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(session.status)}
                            <h4 className="font-medium">{session.name}</h4>
                            <Badge variant={getStatusColor(session.status) as any}>
                              {session.status}
                            </Badge>
                          </div>
                          {session.description && (
                            <p className="text-sm text-muted-foreground">{session.description}</p>
                          )}
                        </div>
                        <div className="text-right text-sm space-y-1">
                          <div>Leads: <span className="font-medium">{session.totalLeadsFound}</span></div>
                          <div>Progress: <span className="font-medium">{session.progress}%</span></div>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-muted-foreground">
                          Created: {new Date(session.createdAt).toLocaleDateString()}
                          {session.lastActivity && (
                            <span className="ml-4">
                              Last Activity: {new Date(session.lastActivity).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          {session.status === 'paused' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => resumeSession(session.id)}
                              disabled={isLoading}
                            >
                              <Play className="h-3 w-3 mr-1" />
                              Resume
                            </Button>
                          )}
                          {session.status === 'processing' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => pauseSession(session.id)}
                              disabled={isLoading}
                            >
                              <Pause className="h-3 w-3 mr-1" />
                              Pause
                            </Button>
                          )}
                          <Button
                            size="sm"
                            onClick={() => {
                              setCurrentSession(session);
                              if (session.totalLeadsFound > 0) {
                                loadSessionLeads(session.id);
                              }
                              setActiveTab('current');
                            }}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Database className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Discovery Sessions</h3>
                  <p className="text-muted-foreground mb-4">Create your first discovery session to get started.</p>
                  <Button onClick={() => setActiveTab('create')}>
                    <Search className="h-4 w-4 mr-2" />
                    Create Session
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Create New Tab */}
        <TabsContent value="create" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Session Creation Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="h-5 w-5" />
                  Create Discovery Session
                </CardTitle>
                <CardDescription>
                  Configure a new persistent discovery session
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="sessionName">Session Name *</Label>
                  <Input
                    id="sessionName"
                    placeholder="e.g., SaaS Companies Q4 2024"
                    value={sessionForm.name}
                    onChange={(e) => setSessionForm(prev => ({
                      ...prev,
                      name: e.target.value
                    }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sessionDescription">Description</Label>
                  <Textarea
                    id="sessionDescription"
                    placeholder="Optional description of this discovery session..."
                    value={sessionForm.description}
                    onChange={(e) => setSessionForm(prev => ({
                      ...prev,
                      description: e.target.value
                    }))}
                  />
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label htmlFor="targetCount">Target Lead Count</Label>
                  <Input
                    id="targetCount"
                    type="number"
                    value={sessionForm.discoveryConfig.targetCount}
                    onChange={(e) => setSessionForm(prev => ({
                      ...prev,
                      discoveryConfig: {
                        ...prev.discoveryConfig,
                        targetCount: parseInt(e.target.value) || 100
                      }
                    }))}
                    min={10}
                    max={1000}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Target Industries</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {['Technology', 'SaaS', 'E-commerce', 'Healthcare', 'Manufacturing', 'Finance'].map(industry => (
                      <div key={industry} className="flex items-center space-x-2">
                        <Checkbox
                          id={industry}
                          checked={sessionForm.discoveryConfig.industries?.includes(industry) || false}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSessionForm(prev => ({
                                ...prev,
                                discoveryConfig: {
                                  ...prev.discoveryConfig,
                                  industries: [...(prev.discoveryConfig.industries || []), industry]
                                }
                              }));
                            } else {
                              setSessionForm(prev => ({
                                ...prev,
                                discoveryConfig: {
                                  ...prev.discoveryConfig,
                                  industries: prev.discoveryConfig.industries?.filter(i => i !== industry) || []
                                }
                              }));
                            }
                          }}
                        />
                        <Label htmlFor={industry} className="text-sm">{industry}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Company Size</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {['startup', 'small-business', 'mid-market', 'enterprise'].map(size => (
                      <div key={size} className="flex items-center space-x-2">
                        <Checkbox
                          id={size}
                          checked={sessionForm.discoveryConfig.companySize?.includes(size) || false}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSessionForm(prev => ({
                                ...prev,
                                discoveryConfig: {
                                  ...prev.discoveryConfig,
                                  companySize: [...(prev.discoveryConfig.companySize || []), size]
                                }
                              }));
                            } else {
                              setSessionForm(prev => ({
                                ...prev,
                                discoveryConfig: {
                                  ...prev.discoveryConfig,
                                  companySize: prev.discoveryConfig.companySize?.filter(s => s !== size) || []
                                }
                              }));
                            }
                          }}
                        />
                        <Label htmlFor={size} className="text-sm capitalize">{size.replace('-', ' ')}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Target Geography</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {['North America', 'Europe', 'Asia Pacific', 'Global'].map(geo => (
                      <div key={geo} className="flex items-center space-x-2">
                        <Checkbox
                          id={geo}
                          checked={sessionForm.discoveryConfig.geography?.includes(geo) || false}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSessionForm(prev => ({
                                ...prev,
                                discoveryConfig: {
                                  ...prev.discoveryConfig,
                                  geography: [...(prev.discoveryConfig.geography || []), geo]
                                }
                              }));
                            } else {
                              setSessionForm(prev => ({
                                ...prev,
                                discoveryConfig: {
                                  ...prev.discoveryConfig,
                                  geography: prev.discoveryConfig.geography?.filter(g => g !== geo) || []
                                }
                              }));
                            }
                          }}
                        />
                        <Label htmlFor={geo} className="text-sm">{geo}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="customCriteria">Custom Criteria</Label>
                  <Textarea
                    id="customCriteria"
                    placeholder="Additional criteria for lead discovery..."
                    value={sessionForm.discoveryConfig.customCriteria}
                    onChange={(e) => setSessionForm(prev => ({
                      ...prev,
                      discoveryConfig: {
                        ...prev.discoveryConfig,
                        customCriteria: e.target.value
                      }
                    }))}
                  />
                </div>

                <Button 
                  onClick={createSession} 
                  disabled={isCreating || !sessionForm.name.trim()}
                  className="w-full"
                >
                  {isCreating ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Creating Session...
                    </>
                  ) : (
                    <>
                      <Database className="h-4 w-4 mr-2" />
                      Create Discovery Session
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Session Templates */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Session Templates
                </CardTitle>
                <CardDescription>
                  Quick start templates for common discovery scenarios
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  {
                    title: "Web Development Agency",
                    description: "Target small businesses needing websites",
                    config: {
                      industries: ["Retail", "Professional Services"],
                      companySize: ["startup", "small-business"],
                      geography: ["North America"]
                    }
                  },
                  {
                    title: "Digital Marketing Agency", 
                    description: "Target growing SaaS companies",
                    config: {
                      industries: ["Technology", "SaaS"],
                      companySize: ["startup", "mid-market"],
                      geography: ["North America", "Europe"]
                    }
                  },
                  {
                    title: "Enterprise Software Sales",
                    description: "Target large companies for enterprise solutions",
                    config: {
                      industries: ["Technology", "Finance", "Healthcare"],
                      companySize: ["mid-market", "enterprise"],
                      geography: ["Global"]
                    }
                  }
                ].map((template, index) => (
                  <div key={index} className="p-3 border rounded-lg space-y-2">
                    <h4 className="font-medium">{template.title}</h4>
                    <p className="text-sm text-muted-foreground">{template.description}</p>
                    <div className="flex flex-wrap gap-1">
                      {template.config.industries.map(industry => (
                        <Badge key={industry} variant="secondary" className="text-xs">
                          {industry}
                        </Badge>
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSessionForm(prev => ({
                          ...prev,
                          name: template.title,
                          description: template.description,
                          discoveryConfig: {
                            ...prev.discoveryConfig,
                            ...template.config
                          }
                        }));
                      }}
                    >
                      Use This Template
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Current Session Tab */}
        <TabsContent value="current" className="space-y-6">
          {currentSession ? (
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {getStatusIcon(currentSession.status)}
                    {currentSession.name}
                  </CardTitle>
                  <CardDescription>
                    <Badge variant={getStatusColor(currentSession.status) as any}>
                      {currentSession.status.charAt(0).toUpperCase() + currentSession.status.slice(1)}
                    </Badge>
                    <span className="ml-2 text-muted-foreground">
                      Session #{currentSession.id}
                    </span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {currentSession.description && (
                    <p className="text-sm text-muted-foreground">{currentSession.description}</p>
                  )}
                  
                  {/* Enhanced Progress Display */}
                  {currentSession.status === 'processing' && realtimeProgress ? (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Overall Progress:</span>
                          <span>{realtimeProgress.detailedProgress?.overallProgress || currentSession.progress}%</span>
                        </div>
                        <Progress value={realtimeProgress.detailedProgress?.overallProgress || currentSession.progress} className="w-full" />
                        
                        {realtimeProgress.detailedProgress?.message && (
                          <p className="text-sm text-muted-foreground">
                            {realtimeProgress.detailedProgress.message}
                          </p>
                        )}
                      </div>

                      {/* Source Progress */}
                      {realtimeProgress.detailedProgress?.sources && realtimeProgress.detailedProgress.sources.length > 0 && (
                        <div className="space-y-3">
                          <h4 className="text-sm font-medium">Source Progress</h4>
                          {realtimeProgress.detailedProgress.sources.map((source: any, index: number) => (
                            <div key={source.type || index} className="space-y-2">
                              <div className="flex justify-between items-center text-sm">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{source.name}</span>
                                  <Badge 
                                    variant={source.status === 'processing' ? 'default' : source.status === 'completed' ? 'secondary' : 'outline'}
                                    className="text-xs"
                                  >
                                    {source.status}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  {source.leadsFound > 0 && (
                                    <span className="flex items-center gap-1">
                                      <Users className="h-3 w-3" />
                                      {source.leadsFound}
                                    </span>
                                  )}
                                  <span>{source.progress || 0}%</span>
                                </div>
                              </div>
                              <Progress value={source.progress || 0} className="w-full h-2" />
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Recent Activity */}
                      {realtimeProgress.detailedProgress?.recentActivity && realtimeProgress.detailedProgress.recentActivity.length > 0 && (
                        <div className="space-y-3">
                          <h4 className="text-sm font-medium">Recent Activity</h4>
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {realtimeProgress.detailedProgress.recentActivity.slice(0, 5).map((activity: any, index: number) => (
                              <div key={index} className="flex items-start gap-2 text-xs">
                                <div className="flex-shrink-0 mt-1">
                                  {activity.type === 'leads_found' ? (
                                    <CheckCircle className="h-3 w-3 text-green-500" />
                                  ) : activity.type === 'processing' ? (
                                    <RefreshCw className="h-3 w-3 text-blue-500 animate-spin" />
                                  ) : (
                                    <Clock className="h-3 w-3 text-gray-400" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <p className="text-muted-foreground">{activity.message}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {new Date(activity.time).toLocaleTimeString()}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Analytics */}
                      {realtimeProgress.analytics && (
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {realtimeProgress.analytics.leadsPerMinute > 0 && (
                            <div>
                              <span className="text-muted-foreground">Rate:</span>
                              <p className="font-medium">{realtimeProgress.analytics.leadsPerMinute} leads/min</p>
                            </div>
                          )}
                          {realtimeProgress.analytics.estimatedCompletion && (
                            <div>
                              <span className="text-muted-foreground">ETA:</span>
                              <p className="font-medium">
                                {new Date(realtimeProgress.analytics.estimatedCompletion).toLocaleTimeString()}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Progress:</span>
                        <span>{currentSession.progress}%</span>
                      </div>
                      <Progress value={currentSession.progress} className="w-full" />
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Leads Found:</span>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{currentSession.totalLeadsFound}</p>
                        {currentSession.status === 'processing' && realtimeProgress?.recentLeads && realtimeProgress.recentLeads.length > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            +{realtimeProgress.recentLeads.length} recent
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Target:</span>
                      <p className="font-medium">{currentSession.discoveryConfig.targetCount}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Created:</span>
                      <p className="font-medium">{new Date(currentSession.createdAt).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Last Activity:</span>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{new Date(currentSession.lastActivity).toLocaleDateString()}</p>
                        {currentSession.status === 'processing' && (
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                            <span className="text-xs text-green-600">Live</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {currentSession.status === 'paused' && (
                      <Button 
                        onClick={() => resumeSession(currentSession.id)}
                        disabled={isLoading}
                      >
                        <Play className="h-4 w-4 mr-2" />
                        Resume Discovery
                      </Button>
                    )}
                    {currentSession.status === 'processing' && (
                      <Button 
                        variant="outline"
                        onClick={() => pauseSession(currentSession.id)}
                        disabled={isLoading}
                      >
                        <Pause className="h-4 w-4 mr-2" />
                        Pause Discovery
                      </Button>
                    )}
                    {currentSession.totalLeadsFound > 0 && (
                      <Button 
                        onClick={() => {
                          loadSessionLeads(currentSession.id);
                          setActiveTab('leads');
                        }}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View Leads ({currentSession.totalLeadsFound})
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Discovery Configuration</CardTitle>
                  <CardDescription>
                    Session parameters and target criteria
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    {currentSession.discoveryConfig.industries && currentSession.discoveryConfig.industries.length > 0 && (
                      <div>
                        <h4 className="font-medium text-sm mb-2">Target Industries</h4>
                        <div className="flex flex-wrap gap-1">
                          {currentSession.discoveryConfig.industries.map(industry => (
                            <Badge key={industry} variant="secondary" className="text-xs">
                              {industry}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {currentSession.discoveryConfig.companySize && currentSession.discoveryConfig.companySize.length > 0 && (
                      <div>
                        <h4 className="font-medium text-sm mb-2">Company Size</h4>
                        <div className="flex flex-wrap gap-1">
                          {currentSession.discoveryConfig.companySize.map(size => (
                            <Badge key={size} variant="outline" className="text-xs">
                              {size.replace('-', ' ')}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {currentSession.discoveryConfig.geography && currentSession.discoveryConfig.geography.length > 0 && (
                      <div>
                        <h4 className="font-medium text-sm mb-2">Geography</h4>
                        <div className="flex flex-wrap gap-1">
                          {currentSession.discoveryConfig.geography.map(geo => (
                            <Badge key={geo} variant="outline" className="text-xs">
                              {geo}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {currentSession.discoveryConfig.customCriteria && (
                      <div>
                        <h4 className="font-medium text-sm mb-2">Custom Criteria</h4>
                        <p className="text-sm text-muted-foreground bg-gray-50 p-2 rounded">
                          {currentSession.discoveryConfig.customCriteria}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Target className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No Active Session</h3>
                <p className="text-muted-foreground mb-4">Select a session from your history or create a new one.</p>
                <div className="flex gap-2">
                  <Button onClick={() => setActiveTab('sessions')}>
                    <History className="h-4 w-4 mr-2" />
                    View Sessions
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('create')}>
                    <Search className="h-4 w-4 mr-2" />
                    Create New
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Discovered Leads Tab */}
        <TabsContent value="leads" className="space-y-6">
          {currentSession && sessionLeads.length > 0 ? (
            <>
              {/* Session Stats */}
              {sessionStats && (
                <div className="grid md:grid-cols-4 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Total Leads</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{sessionStats.totalLeads}</div>
                      <p className="text-xs text-muted-foreground">
                        Avg Score: {sessionStats.averageScore.toFixed(1)}
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Selected</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{selectedLeads.size}</div>
                      <div className="flex gap-1 mt-2">
                        <Button size="sm" variant="outline" onClick={selectAllLeads}>
                          Select All
                        </Button>
                        <Button size="sm" variant="outline" onClick={clearSelection}>
                          Clear
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Imported</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{sessionStats.importedLeads}</div>
                      {selectedLeads.size > 0 && (
                        <Button 
                          size="sm" 
                          className="mt-2 w-full"
                          onClick={importSelectedLeads}
                          disabled={isLoading}
                        >
                          <Import className="h-3 w-3 mr-1" />
                          Import ({selectedLeads.size})
                        </Button>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Filters</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Label htmlFor="minScore" className="text-xs">Min Score:</Label>
                          <Input
                            id="minScore"
                            type="number"
                            value={leadFilters.minScore}
                            onChange={(e) => setLeadFilters(prev => ({
                              ...prev,
                              minScore: parseInt(e.target.value) || 0
                            }))}
                            className="h-6 text-xs"
                            min={0}
                            max={100}
                          />
                        </div>
                        <Button 
                          size="sm" 
                          className="w-full"
                          onClick={() => currentSession && loadSessionLeads(currentSession.id)}
                          disabled={isLoading}
                        >
                          <Filter className="h-3 w-3 mr-1" />
                          Apply
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Leads List */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Discovered Leads ({sessionLeads.length})</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {sessionLeads.map((lead) => (
                      <div 
                        key={lead.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedLeads.has(lead.id) ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => toggleLeadSelection(lead.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="space-y-3 flex-1">
                            <div className="flex items-center gap-3">
                              <Checkbox
                                checked={selectedLeads.has(lead.id)}
                                onCheckedChange={() => toggleLeadSelection(lead.id)}
                              />
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium">
                                  {lead.name || 'Unknown Name'}
                                  {lead.company && ` - ${lead.company}`}
                                </h4>
                                {lead.isImported && (
                                  <Badge variant="secondary" className="text-xs">
                                    Imported
                                  </Badge>
                                )}
                              </div>
                              <Badge className={`ml-auto ${getScoreColor(Number(lead.aiScore))} border`}>
                                Score: {lead.aiScore}
                              </Badge>
                            </div>
                            
                            {/* Detailed Lead Information */}
                            <div className="grid md:grid-cols-3 gap-3 text-sm">
                              {lead.jobTitle && (
                                <div className="flex items-center gap-2">
                                  <Users className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                  <span className="truncate">{lead.jobTitle}</span>
                                </div>
                              )}
                              {lead.industry && (
                                <div className="flex items-center gap-2">
                                  <Building2 className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                  <span className="truncate">{lead.industry}</span>
                                </div>
                              )}
                              {lead.companySize && (
                                <div className="flex items-center gap-2">
                                  <TrendingUp className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                  <span className="truncate">{lead.companySize}</span>
                                </div>
                              )}
                              {lead.email && (
                                <div className="flex items-center gap-2">
                                  <Mail className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                  <span className="truncate">{lead.email}</span>
                                </div>
                              )}
                              {lead.phone && (
                                <div className="flex items-center gap-2">
                                  <Phone className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                  <span className="truncate">{lead.phone}</span>
                                </div>
                              )}
                              {lead.location && (
                                <div className="flex items-center gap-2">
                                  <MapPin className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                  <span className="truncate">{lead.location}</span>
                                </div>
                              )}
                              {lead.website && (
                                <div className="flex items-center gap-2">
                                  <Globe className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                  <a 
                                    href={lead.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="truncate hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {lead.website}
                                  </a>
                                </div>
                              )}
                              {lead.linkedinUrl && (
                                <div className="flex items-center gap-2">
                                  <Linkedin className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                  <a 
                                    href={lead.linkedinUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="truncate hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    LinkedIn Profile
                                  </a>
                                </div>
                              )}
                              {lead.sourceUrl && (
                                <div className="flex items-center gap-2">
                                  <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                  <a 
                                    href={lead.sourceUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="truncate hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    Source Link
                                  </a>
                                </div>
                              )}
                            </div>

                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Target className="h-3 w-3" />
                                <span>Source: {lead.source}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                <span>Discovered: {new Date(lead.createdAt).toLocaleDateString()}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Star className="h-3 w-3" />
                                <span>Confidence: {lead.confidence}%</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Users className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No Leads Available</h3>
                <p className="text-muted-foreground mb-4">
                  {currentSession 
                    ? `Session "${currentSession.name}" hasn't discovered any leads yet.`
                    : "Select an active session to view discovered leads."
                  }
                </p>
                <div className="flex gap-2">
                  <Button onClick={() => setActiveTab('sessions')}>
                    <History className="h-4 w-4 mr-2" />
                    View Sessions
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('create')}>
                    <Search className="h-4 w-4 mr-2" />
                    Create New Session
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}